import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import {
  addNewChatBot,
  addNewChatTopic,
  addNewGroup,
  addNewMessage,
  addNewMessageInChat,
  addNewMessageInChatSlow,
  addNewPersonalGroup,
  addThinkingMessage,
  changeStatusSelectedChat,
  deleteChatGroup,
  getCountNewMessages,
  readMessagesInChat,
  replaceThinkingMessage,
  setSelectedUser,
  updateChatGroupInfo,
  updateCompanyTokenQuota,
} from '@/store/chat/actions';
import { getUserIncome } from '@/store/income/actions';
import { logout } from '@/store/login/actions';
import { clearSearchParams, clearSearchText, clearSearchUserText } from '@/store/search/actions';
import { clearTopicFilter, clearTopics } from '@/store/topics/actions';
import { clearUserTopicFilter } from '@/store/user-topics/actions';

import { selectBadge } from '@/store/badge/selectors';
import { selectChat } from '@/store/chat/selectors';
import { selectNotifications } from '@/store/notifications-new/selectors';
import { selectUsers } from '@/store/users/selectors';

import { Badge, ConfigProvider, Drawer, Dropdown, Flex, Layout, Menu, Row, Spin, Tooltip } from 'antd';
import CoinInfoModal from '@/pages//CoinInfoModal';
import ModalFlower from '@/pages//ModalFlower';
import CoinHistoryModal from '@/pages/CoinHistoryModal';
import { Avatar } from '@/components';
import BadgeModal from '@/components/BadgeModal';
import { CoinsIcon } from '@/components/elements';
import { useNotificationTypes } from '@/components/NotificationTypes';
import { MenuOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import { BsChat } from 'react-icons/bs';
import { CgLogOut } from 'react-icons/cg';
import { FiInbox } from 'react-icons/fi';
import {
  HiOutlineBell,
  HiOutlineCash,
  HiOutlineStar,
  HiOutlineUserCircle,
  HiOutlineUserGroup,
  HiOutlineViewGrid,
} from 'react-icons/hi';

import TradeLink from './TradeLink';

import chatThinkingManager from '@/utils/ChatThinkingManager';
import { clearStorage } from '@/utils/clearStorage';
import { unsubscribeDevice } from '@/utils/pushManager';
import { goToAdminPage } from '@/utils/redirect';
import BadgeElement from '@shared/components/BadgeElement';
import { useLocalFileByUUID } from '@shared/hooks/localFiles';
import useIsMobile from '@shared/hooks/useIsMobile';

import { THINKING_CONFIG } from '@/config/thinkingConfig';
import Utils from '@/Utils';
import classNames from 'classnames';
import dayjs from 'dayjs';
import _ from 'lodash';

import css from './Header.module.scss';

import { ACCESS_TO_ADMIN_FROM_USERS } from '@/constants/permissions';
import bellIcon from '@/assets/images/bell.svg';
import logoDefault from '@/assets/images/logo_default.png';

const { Header } = Layout;

const MAX_LENGTH_NAME = 17;

export const AppHeader = ({ openBurgerMenu, setOpenBurgerMenu, socket }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const carouselRef = useRef();

  const [coinInfoModalVisible, setCoinInfoModalVisible] = useState(false);
  const [coinHistoryModalVisible, setCoinHistoryModalVisible] = useState(false);
  const [badgeModalVisible, setBadgeModalVisible] = useState(false);
  const [modalFlower, setModalFlower] = useState(false);
  const [activeTopMenuKeys, setActiveTopMenuKeys] = useState('');
  const [allTradeLinks, setAllTradeLinks] = useState([]);

  const { currentUser } = useSelector(selectUsers);
  const { unReadNotifications, isUnReadNotificationLoading } = useSelector(selectNotifications);
  const { selectedUser, countNewMessage, selectedChatTopic } = useSelector(selectChat);
  const { badges } = useSelector(selectBadge);

  const [uniqNotif, setUniqNotif] = useState(null);
  const [visible, setVisible] = useState(false);
  const { t } = useTranslation('header');

  const [topBanner] = useLocalFileByUUID(currentUser?.domainCompany?.banner?.uuid, {
    isDefaultNull: true,
  });
  const [imageLogo, isLoadingImageLogo] = useLocalFileByUUID(currentUser?.domainCompany.logo, {
    defaultImg: logoDefault,
    isDefaultNull: true,
  });

  const [isMobile] = useIsMobile(1024);
  const isOnline = Utils.isOnline();

  const onHandlerMessage = () => {
    if (process.env.NODE_ENV === 'development' && socket) {
      window.socketInstance = socket;
    }

    socket?.on('newMessage', newMessage => {
      if (
        newMessage?.messageData?.chatId === selectedUser?.chatId ||
        newMessage?.messageData?.chatId === selectedChatTopic?.chatId
      ) {
        dispatch(addNewMessageInChat(newMessage));
      } else {
        dispatch(addNewMessage(newMessage));
      }
    });

    socket?.on('thinkingMessage', thinkingData => {
      if (thinkingData?.chatId === selectedUser?.chatId || thinkingData?.chatId === selectedChatTopic?.chatId) {
        const isBotChat =
          selectedUser?.bot?.botId ||
          selectedUser?.botId ||
          selectedChatTopic?.bot?.botId ||
          selectedChatTopic?.botId ||
          selectedUser?.isBot === true ||
          selectedChatTopic?.isBot === true ||
          selectedUser?.userType === 'bot';

        if (isBotChat) {
          chatThinkingManager.addThinkingMessage(
            thinkingData.messageId,
            thinkingData.chatId,
            (messageId, chatId, isFinal = false) => {
              const timeoutMessage = {
                messageId: `timeout-${Date.now()}`,
                content: isFinal ? THINKING_CONFIG.MESSAGES.LONG_OPERATION : THINKING_CONFIG.MESSAGES.TIMEOUT,
                createdAt: new Date().toISOString(),
                isBot: true,
                isError: false,
                isThinking: !isFinal,
                isTimeout: true,
                isWaiting: true,
              };
              dispatch(replaceThinkingMessage(chatId, messageId, timeoutMessage));
            },
            (messageId, chatId, extensionCount) => {
              const extendMessage = {
                messageId: thinkingData.messageId,
                content:
                  extensionCount === 1
                    ? THINKING_CONFIG.MESSAGES.EXTENDED_THINKING
                    : THINKING_CONFIG.MESSAGES.LONG_OPERATION,
                createdAt: new Date().toISOString(),
                isBot: true,
                isError: false,
                isThinking: true,
                isExtended: true,
                extensionCount,
              };
              dispatch(replaceThinkingMessage(chatId, messageId, extendMessage));
            }
          );

          dispatch(addThinkingMessage(thinkingData.chatId, thinkingData));
        }
      }
    });

    socket?.on('replaceThinkingMessage', replaceData => {
      if (replaceData?.chatId === selectedUser?.chatId || replaceData?.chatId === selectedChatTopic?.chatId) {
        const isBotChat =
          selectedUser?.bot?.botId ||
          selectedUser?.botId ||
          selectedChatTopic?.bot?.botId ||
          selectedChatTopic?.botId ||
          selectedUser?.isBot === true ||
          selectedChatTopic?.isBot === true ||
          selectedUser?.userType === 'bot';

        if (isBotChat) {
          chatThinkingManager.replaceThinkingMessage(replaceData.oldMessageId, replaceData.newMessage.messageId);
          dispatch(replaceThinkingMessage(replaceData.chatId, replaceData.oldMessageId, replaceData.newMessage));
        }
      }
    });

    socket?.on('think', thinkingData => {
      if (thinkingData?.chatId === selectedUser?.chatId || thinkingData?.chatId === selectedChatTopic?.chatId) {
        const isBotChat =
          selectedUser?.bot?.botId ||
          selectedUser?.botId ||
          selectedChatTopic?.bot?.botId ||
          selectedChatTopic?.botId ||
          selectedUser?.isBot === true ||
          selectedChatTopic?.isBot === true ||
          selectedUser?.userType === 'bot';

        if (isBotChat) {
          const thinkingMessageData = chatThinkingManager.constructor.createThinkingMessageData(
            thinkingData.chatId,
            THINKING_CONFIG.MESSAGES.DEFAULT_THINKING,
            'Бот'
          );

          chatThinkingManager.addThinkingMessage(
            thinkingMessageData.messageId,
            thinkingData.chatId,
            (messageId, chatId, isFinal = false) => {
              const timeoutMessage = {
                messageId: `timeout-${Date.now()}`,
                content: isFinal ? THINKING_CONFIG.MESSAGES.LONG_OPERATION : THINKING_CONFIG.MESSAGES.TIMEOUT,
                createdAt: new Date().toISOString(),
                isBot: true,
                isError: false,
                isThinking: !isFinal,
                isTimeout: true,
                isWaiting: true,
              };
              dispatch(replaceThinkingMessage(chatId, messageId, timeoutMessage));
            },
            (messageId, chatId, extensionCount) => {
              const extendMessage = {
                messageId: thinkingMessageData.messageId,
                content:
                  extensionCount === 1
                    ? THINKING_CONFIG.MESSAGES.EXTENDED_THINKING
                    : THINKING_CONFIG.MESSAGES.LONG_OPERATION,
                createdAt: new Date().toISOString(),
                isBot: true,
                isError: false,
                isThinking: true,
                isExtended: true,
                extensionCount,
              };
              dispatch(replaceThinkingMessage(chatId, messageId, extendMessage));
            }
          );

          dispatch(addThinkingMessage(thinkingData.chatId, thinkingMessageData));
        }
      }
    });

    socket?.on('agentWorking', thinkingData => {
      if (thinkingData?.chatId === selectedUser?.chatId || thinkingData?.chatId === selectedChatTopic?.chatId) {
        const isBotChat =
          selectedUser?.bot?.botId ||
          selectedUser?.botId ||
          selectedChatTopic?.bot?.botId ||
          selectedChatTopic?.botId ||
          selectedUser?.isBot === true ||
          selectedChatTopic?.isBot === true ||
          selectedUser?.userType === 'bot';

        if (isBotChat) {
          const thinkingMessageData = chatThinkingManager.constructor.createThinkingMessageData(
            thinkingData.chatId,
            THINKING_CONFIG.MESSAGES.AGENT_THINKING,
            'Агент'
          );

          chatThinkingManager.addThinkingMessage(
            thinkingMessageData.messageId,
            thinkingData.chatId,
            (messageId, chatId, isFinal = false) => {
              const timeoutMessage = {
                messageId: `timeout-${Date.now()}`,
                content: isFinal ? THINKING_CONFIG.MESSAGES.LONG_OPERATION : THINKING_CONFIG.MESSAGES.AGENT_TIMEOUT,
                createdAt: new Date().toISOString(),
                isBot: true,
                isError: false,
                isThinking: !isFinal,
                isTimeout: true,
                isWaiting: true,
              };
              dispatch(replaceThinkingMessage(chatId, messageId, timeoutMessage));
            },
            (messageId, chatId, extensionCount) => {
              const extendMessage = {
                messageId: thinkingMessageData.messageId,
                content:
                  extensionCount === 1
                    ? THINKING_CONFIG.MESSAGES.EXTENDED_THINKING
                    : THINKING_CONFIG.MESSAGES.LONG_OPERATION,
                createdAt: new Date().toISOString(),
                isBot: true,
                isError: false,
                isThinking: true,
                isExtended: true,
                extensionCount,
              };
              dispatch(replaceThinkingMessage(chatId, messageId, extendMessage));
            }
          );

          dispatch(addThinkingMessage(thinkingData.chatId, thinkingMessageData));
        }
      }
    });

    socket?.on('newPersonalChat', newGroup => {
      if (location.pathname.includes('chat/personal')) {
        dispatch(addNewPersonalGroup(newGroup));

        if (selectedUser.id === newGroup.userData.id) {
          dispatch(setSelectedUser({ ...selectedUser, chatId: newGroup.chatId }));
          dispatch(addNewMessageInChat(newGroup?.lastMessage));
        }
      }
    });

    socket?.on('onReadMessages', data => {
      dispatch(readMessagesInChat(data));
    });

    socket?.on('newChat', newGroup => {
      if (location.pathname.includes('chat/groups')) dispatch(addNewGroup(newGroup));
    });

    socket?.on('deleteChat', data => {
      if (location.pathname.includes('chat/groups')) dispatch(deleteChatGroup(data));
    });

    socket?.on('updateChat', newData => {
      if (location.pathname.includes('chat/groups')) dispatch(updateChatGroupInfo(newData));
    });

    socket?.on('newTopic', newTopic => {
      if (location.pathname.includes('chat/groups') && selectedUser?.chatId === newTopic?.parentChatId)
        dispatch(addNewChatTopic(newTopic));
    });

    socket?.on('changeStatusChat', data => {
      if (location.pathname.includes('chat/groups') && selectedUser?.chatId === data?.chatId)
        dispatch(changeStatusSelectedChat());
    });

    socket?.on('newMessageSlow', newMessage => {
      if (
        newMessage?.messageData?.chatId === selectedUser?.chatId ||
        newMessage?.messageData?.chatId === selectedChatTopic?.chatId
      ) {
        dispatch(addNewMessageInChatSlow(newMessage));
      } else {
        dispatch(addNewMessage(newMessage));
      }
    });

    socket?.on('newChatBot', newBot => {
      dispatch(addNewChatBot(newBot));
    });

    socket?.on('companyTokenQuota', quotaData => {
      dispatch(updateCompanyTokenQuota(quotaData));
    });
  };

  const offHandlerMessage = () => {
    socket?.off('newMessage');
    socket?.off('thinkingMessage');
    socket?.off('replaceThinkingMessage');
    socket?.off('think');
    socket?.off('agentWorking');
    socket?.off('newPersonalChat');
    socket?.off('newChat');
    socket?.off('readMessage');
    socket?.off('deleteChat');
    socket?.off('updateChat');
    socket?.off('newTopic');
    socket?.off('changeStatusChat');
    socket?.off('newMessageSlow');
    socket?.off('newChatBot');
    socket?.off('companyTokenQuota');
  };

  const handleCancel = () => {
    setCoinInfoModalVisible(false);
    setCoinHistoryModalVisible(false);
  };

  const openCoinHistoryModal = () => {
    setCoinHistoryModalVisible(true);
    dispatch(getUserIncome(currentUser.id, 0));
  };

  const onLogOut = () => {
    socket?.disconnect();
    unsubscribeDevice();
    dispatch(
      logout(() => {
        clearStorage();
        navigate('/user-login');
      })
    );
  };

  const handleCloseFlower = () => {
    setModalFlower(false);
  };

  const clearFilterCollection = () => {
    dispatch(clearUserTopicFilter());
    dispatch(clearSearchUserText());
  };

  const planningItem = {
    key: '/planning',
    label: t('planning'),
    onClick: () => {
      handleProfileMenuClick(true);
      navigate('/planning');
    },
  };

  const myPlanMenuFunc = () => {
    const children = [];

    children.push(planningItem);

    if (currentUser?.domainCompany?.hasIpr) {
      children.push({
        key: '/career-plan',
        label: t('careerPlan'),
        onClick: () => navigate('/career-plan'),
      });
    }

    return children;
  };

  const myPlanChildren = myPlanMenuFunc();

  const trajectoryEnableItems =
    myPlanChildren.length === 1
      ? { ...planningItem, label: t('plan') }
      : {
          key: 'mainSubMenu-1',
          label: t('plan'),
          children: myPlanChildren,
        };

  const topMainMenu = [
    trajectoryEnableItems,
    {
      key: '/',
      label: t('collectionTopics'),
      children: [
        {
          key: '/collection-topics',
          label: t('collectionTopics'),
          onClick: () => {
            handleProfileMenuClick(true);
            navigate('/collection-topics');
          },
        },
        ...(currentUser?.domainCompany?.showTracks
          ? [
              {
                key: '/collection-tracks',
                label: t('collectionTracks'),
                onClick: () => navigate('/collection-tracks'),
              },
            ]
          : []),
        {
          key: '/calendar',
          label: t('eventCalendar'),
          onClick: () => {
            handleProfileMenuClick(true);
            navigate('/calendar');
          },
        },
      ],
    },

    ...(currentUser?.domainCompany?.hasCompilation
      ? [
          {
            key: '/compilations',
            label: t('compilations'),
            onClick: () => {
              handleProfileMenuClick(true);
              navigate('/compilations');
            },
          },
        ]
      : []),

    // Закомментированно в задаче https://kampus.atlassian.net/browse/CLOUD-2657, так как возможны возвращения d360 и инструкции
    // {
    //   key: '/d360v2',
    //   label: t('d360v2'),
    //   children: [
    //     {
    //       key: '/d360v2',
    //       label: t('d360v2'),
    //       onClick: () => {
    //         handleProfileMenuClick(true);
    //         navigate('/d360v2');
    //       },
    //     },
    //     {
    //       key: '/instructions',
    //       label: t('instructions'),
    //       onClick: () => {
    //         handleProfileMenuClick(true);
    //         window.location.replace('https://study-video.severstal.com/Videocontent/kompas/');
    //       },
    //     },
    //   ],
    // },

    ...(currentUser?.domainCompany?.hasMentors
      ? [
          {
            key: '/mentors',
            label: t('mentors'),
            onClick: () => {
              clearFilterCollection();
              handleProfileMenuClick(true);
              navigate('/mentors');
            },
          },
        ]
      : []),
    ...(currentUser?.domainCompany?.hasShop
      ? [
          {
            key: '/shop',
            label: t('shop'),
            onClick: () => {
              handleProfileMenuClick(true);
              navigate('/shop');
            },
          },
        ]
      : []),
    ...allTradeLinks,
  ];

  const domain = _.get(currentUser?.domainCompany, 'adminDomain');
  const onClickHandler = useCallback(() => setVisible(!visible), [visible]);
  const [showNotificationContent, changeShowNotificationContent] = useState(false);

  const dropdownMenu = [
    ...(currentUser?.authorities.includes(ACCESS_TO_ADMIN_FROM_USERS) && !isMobile
      ? [
          {
            key: 0,
            icon: <HiOutlineViewGrid />,
            onClick: () => {
              handleProfileMenuClick();
              goToAdminPage(domain, '/');
            },
            label: Utils.isManager(currentUser) ? t('managerOffice') : t('admin'),
            id: 'headerLoginToAdminBtn',
          },
          {
            type: 'divider',
          },
        ]
      : []),
    {
      key: 1,
      icon: <HiOutlineUserCircle />,
      onClick: () => {
        handleProfileMenuClick();
        navigate('/my-profile');
      },
      label: t('myProfile'),
    },
    {
      key: 2,
      icon: <FiInbox />,
      onClick: () => {
        handleProfileMenuClick();
        navigate('/news');
      },
      label: t('news'),
    },
    ...(currentUser?.domainCompany?.hasCoins
      ? [
          {
            key: 3,
            icon: <HiOutlineCash />,
            onClick: () => {
              handleProfileMenuClick();
              openCoinHistoryModal();
            },
            label: t('myCoins'),
            dataQa: 'myCoinsMenuItem',
          },
        ]
      : []),
    {
      key: 5,
      icon: <HiOutlineBell />,
      onClick: () => {
        handleProfileMenuClick();
        navigate('/my-notifications');
      },
      className: 'menuOverflowChange',
      label: (
        <Badge
          className={css['Dropdown__notifications-bell']}
          count={unReadNotifications?.length ? unReadNotifications.length : 0}
          overflowCount={99}
          offset={[5, -7]}
        >
          {t('notifications')}
        </Badge>
      ),
    },
    {
      key: 8,
      icon: <HiOutlineStar />,
      onClick: () => {
        handleProfileMenuClick();
        navigate('/my-collection');
      },
      label: t('favorites'),
    },
    ...(currentUser?.domainCompany?.showColleagues
      ? [
          {
            key: 9,
            icon: <HiOutlineUserGroup />,
            onClick: () => {
              handleProfileMenuClick();
              navigate('/collegues');
            },
            label: t('myColleagues'),
          },
        ]
      : []),
    {
      type: 'divider',
    },
  ];

  const dropdownMenuExit = {
    key: 10,
    icon: <CgLogOut />,
    onClick: onLogOut,
    label: t('exit'),
    id: 'headerUserDropdownMenuExitBtn',
  };

  const mobileMenu = [
    {
      key: 'profile',
      className: css['App-mobile-header-dropdown-item'],
      onClick: () => navigate('/my-profile'),
      label: (
        <span className={css['App-personal-info']}>
          <div className={css['App-personal-name']} id='App-personal-name'>
            {currentUser &&
              currentUser.firstName &&
              currentUser.lastName &&
              `${currentUser.firstName} ${currentUser.lastName}`}
            {currentUser?.domainCompany?.hasCoins && (
              <div className={css['App-personal-coins']}>
                <div className={css['App-personal-coins-icon']}>
                  <CoinsIcon uuidCoinsIcon={currentUser.domainCompany.coinIcon} />
                </div>
                {_.isNumber(currentUser.account) ? currentUser.account : ''}
              </div>
            )}
          </div>
        </span>
      ),
    },
    {
      type: 'divider',
    },
    ...dropdownMenu,
    ...topMainMenu,
    {
      type: 'divider',
    },
    dropdownMenuExit,
  ];

  const notificationContent = (
    <div className={css['Notification-content']}>
      {unReadNotifications?.length > 0 ? (
        <>
          {unReadNotifications.slice(0, 10).map((itemNotification, index) => {
            const { IconNotification } = useNotificationTypes({
              type: itemNotification.groupTemplateType,
              className: css['Notification-content__item--icon'],
              t,
            });
            return (
              <div
                onClick={() => {
                  navigate(`/my-notifications?modal_id=${itemNotification.id}`);
                  changeShowNotificationContent(false);
                }}
                className={css['Notification-content__item']}
                key={'itemNotification' + index}
              >
                <IconNotification />
                <div className={css['Notification-content__item-block']}>
                  <div className={css['Notification-content__item-block--text']}>
                    {!!itemNotification?.subject && itemNotification?.subject.length > 40
                      ? itemNotification?.subject.slice(0, 37) + '...'
                      : itemNotification?.subject}
                  </div>

                  <div className={css['Notification-content__item-block--time']}>
                    {dayjs(itemNotification.timestamp).fromNow(true)}
                  </div>
                </div>
              </div>
            );
          })}
        </>
      ) : (
        <div className={classNames(css['Notification-content__item'], css['Notification-content--noNew'])}>
          {t('noNewNotification')}
        </div>
      )}
      <div
        onClick={() => {
          navigate('/my-notifications');
          changeShowNotificationContent(false);
        }}
        className={css['Notification-content__item--last']}
        key={'itemNotification-default'}
        align='center'
      >
        {t('showAll')}
      </div>
    </div>
  );

  const handleProfileMenuClick = (topMenu = false) => {
    if (!topMenu) {
      onClickHandler();
    }
    dispatch(clearTopicFilter());
    dispatch(clearSearchText());
    dispatch(clearSearchParams());
  };

  const renderName = () => {
    if (currentUser?.firstName?.length + currentUser?.lastName?.length > MAX_LENGTH_NAME) {
      return `${currentUser.firstName[0]}. ${currentUser?.lastName}`;
    } else {
      return `${currentUser?.firstName} ${currentUser?.lastName}`;
    }
  };

  const renderBadges = () => {
    if (badges?.length > 3) {
      return (
        <Flex>
          {badges?.slice(0, 3)?.map((elem, index) => (
            <div key={index} className={css['App-personal-badge-icon']}>
              <BadgeElement key={index} size='small' data={elem} hideName hasTooltip={false} />
            </div>
          ))}
          <div className={css['App-personal-badge-icon']}>
            <div className={css['App-personal-badge-count']}>{badges.slice(3).length}</div>
          </div>
        </Flex>
      );
    } else {
      return badges?.map((elem, index) => (
        <div key={index} className={css['App-personal-badge-icon']}>
          <BadgeElement size='small' data={elem} hideName hasTooltip={false} />
        </div>
      ));
    }
  };

  useEffect(() => {
    if (unReadNotifications) {
      setUniqNotif([...new Map(unReadNotifications.map(item => [item.id, item])).values()]);
    }
  }, [unReadNotifications]);

  useEffect(() => {
    if (location.pathname?.includes('/track/')) {
      return carouselRef.current?.goTo(0);
    }

    if (location.pathname === '/' || location.pathname?.includes('/collection-topics')) {
      return carouselRef.current?.goTo(1);
    }

    if (location.pathname?.includes('/mentors')) {
      return carouselRef.current?.goTo(2);
    }

    if (location.pathname?.includes('/shop')) {
      return carouselRef.current?.goTo(3);
    }

    if (location.pathname?.includes('/career-plan')) {
      return carouselRef.current?.goTo(4);
    }
  }, [location]);

  useEffect(() => {
    const currentActiveTopMenuKey = location.pathname === '/library' ? 'Libraruru' : location.pathname;
    setActiveTopMenuKeys(currentActiveTopMenuKey);
  }, [location.pathname]);

  useEffect(() => {
    onHandlerMessage();

    return () => {
      offHandlerMessage();
    };
  }, [selectedUser, location, socket, selectedChatTopic]);

  useEffect(() => {
    if (document.location.pathname !== '/' && !document.location.pathname.includes('collection-topics')) {
      dispatch(clearTopics());
    }
  }, [document.location.pathname]);

  useEffect(() => {
    if (isOnline) dispatch(getCountNewMessages());
  }, []);

  useEffect(() => {
    if (currentUser.domainCompany.tradeLinks) {
      const result = currentUser.domainCompany.tradeLinks.map((tradeLink, tradelinkIndex) => {
        if (tradeLink.name && tradeLink.additionalName) {
          return {
            key: tradeLink.additionalName + tradelinkIndex + Symbol('uniq').toString(),
            label: (
              <Tooltip placement='bottom' title={tradeLink.tooltip}>
                <>
                  {tradeLink.url.includes('http') ? (
                    <TradeLink
                      id={tradeLink.id}
                      url={tradeLink.url}
                      name={currentUser.language === 'ru_RU' ? tradeLink.name : tradeLink.additionalName}
                      onClick={() => handleProfileMenuClick(true)}
                    />
                  ) : (
                    <Link to={tradeLink.url} onClick={() => handleProfileMenuClick(true)}>
                      {currentUser.language === 'ru_RU' ? tradeLink.name : tradeLink.additionalName}{' '}
                    </Link>
                  )}
                </>
              </Tooltip>
            ),
          };
        }
      });
      setAllTradeLinks(result);
    }
  }, [currentUser?.language, currentUser?.domainCompany?.tradeLinks]);

  const handleDrawerClose = () => {
    setOpenBurgerMenu(false);
  };

  return (
    <div>
      <Layout>
        <Header id='HeaderMobile' className={css['App-mobile']}>
          <Row className={css['App-mobile-container']} type='flex' justify='space-between' align='middle'>
            <div>
              <Spin spinning={isLoadingImageLogo}>
                <Link to='/collection-topics' className={css['App-mobile-header-link']}>
                  <img className={css['App-mobile-header-logo']} src={imageLogo} />
                </Link>
              </Spin>
            </div>
            <div className={css['App-mobile-header-dropdown']}>
              <Avatar className={css['App-avatar']} user={currentUser} small />
            </div>
            <div
              className={css['App-mobile-menu-button']}
              onClick={() => setOpenBurgerMenu(true)}
              style={{ cursor: 'pointer', padding: '8px' }}
            >
              <MenuOutlined style={{ fontSize: '20px' }} />
            </div>
          </Row>
        </Header>

        <Header className={css['App-header']}>
          <Flex justify='space-between' align='end' className={css['App-header-content']}>
            <Flex justify='flex-start' align='center' className={css['App-header-item__first']}>
              <Spin spinning={isLoadingImageLogo}>
                <Link to='/collection-topics' className={css['App-header-link']}>
                  <img className={css['App-mobile-header-logo']} src={imageLogo} />
                </Link>
              </Spin>
              <Menu
                items={topMainMenu}
                selectedKeys={activeTopMenuKeys}
                className={css['App-header-item__menu']}
                mode='horizontal'
              />
            </Flex>
            <Flex justify='flex-end' align='center' className={css['App-header-item__end']}>
              {currentUser?.domainCompany?.hasChat && (
                <Link
                  align='center'
                  justify='center'
                  to='/chat/personal'
                  className={css['Chat']}
                  data-qa='hasNewMessageHeaderIcon'
                >
                  <BsChat size={26} />
                  {countNewMessage !== 0 && <div className={css['Chat__icon']} data-qa='hasNewMessageBadge'></div>}
                </Link>
              )}
              <Spin size='small' spinning={isUnReadNotificationLoading}>
                <Dropdown
                  popupRender={() => notificationContent}
                  placement='bottom'
                  trigger={['click']}
                  onOpenChange={() => changeShowNotificationContent(!showNotificationContent)}
                  open={showNotificationContent}
                  className={css['Notification']}
                  dropdownAlign={{ offset: [50, 50] }}
                >
                  <Badge
                    className={css['Notification--bell']}
                    count={uniqNotif?.length ? uniqNotif.length : 0}
                    overflowCount={99}
                  >
                    <img src={bellIcon} alt='' className={css['Notification--bell__icon']} />
                  </Badge>
                </Dropdown>
              </Spin>
              {!isMobile && (
                <Dropdown
                  menu={{
                    items: [...dropdownMenu, dropdownMenuExit],
                    onClick: handleProfileMenuClick,
                  }}
                  placement='bottomRight'
                  trigger={['click']}
                  onOpenChange={() => setVisible(false)}
                  open={visible}
                  overlayClassName={css['Dropdown-menu']}
                >
                  <div className={css['App-personal']} onClick={onClickHandler} style={{ cursor: 'pointer' }}>
                    <Avatar className={css['App-avatar']} user={currentUser} small />
                    <span className={css['App-personal-info']}>
                      <div className={css['App-personal-name']} id='App-personal-name' data-qa='userBlock'>
                        {renderName()}
                        {currentUser?.domainCompany && currentUser.domainCompany.hasCoins && (
                          <Tooltip mouseEnterDelay={0.5} title={t('coinHelp')}>
                            <div className={css['App-personal-coins']}>
                              <div className={css['App-personal-coins-icon']}>
                                <CoinsIcon uuidCoinsIcon={currentUser.domainCompany.coinIcon} />
                              </div>
                              {_.isNumber(currentUser.account) ? currentUser.account : ''}
                            </div>
                          </Tooltip>
                        )}
                        {currentUser?.domainCompany?.hasCoins && (
                          <div className={css['App-personal-badge']}>{renderBadges()}</div>
                        )}
                      </div>
                    </span>
                  </div>
                </Dropdown>
              )}
            </Flex>
          </Flex>

          <CoinInfoModal visible={coinInfoModalVisible} onCancel={handleCancel} />

          <CoinHistoryModal open={coinHistoryModalVisible} onCancel={handleCancel} student={currentUser.id} />

          <ModalFlower navigate={navigate} modalFlower={modalFlower} onCancel={handleCloseFlower} />

          <BadgeModal open={badgeModalVisible} onCancel={() => setBadgeModalVisible(false)} />
        </Header>

        {currentUser?.domainCompany?.banner?.showBanner && !location.pathname.includes('chat') && topBanner && (
          <a href={currentUser.domainCompany?.banner?.link} target='_blank' className={css['App--banner']}>
            <img data-qa='topBanner' src={topBanner} className={css['App--banner-img']} />
          </a>
        )}

        <ConfigProvider
          theme={{
            components: {
              Drawer: {
                colorBgElevated: '#fff',
              },
            },
          }}
        >
          <Drawer
            placement='right'
            open={openBurgerMenu}
            closable={false}
            maskClosable={true}
            onClose={handleDrawerClose}
            footer={
              <div className={css['App-mobile-drawer-footer']} onClick={handleDrawerClose}>
                <Tooltip title={t('closeMenu')}>
                  <MenuUnfoldOutlined />
                </Tooltip>
              </div>
            }
          >
            <Menu
              mode='inline'
              items={mobileMenu}
              onClick={handleDrawerClose}
              className={css['App-mobile-drawer-menu']}
            />
          </Drawer>
        </ConfigProvider>
      </Layout>
    </div>
  );
};

export default AppHeader;
